import { useCallback, useEffect, useRef, useState } from 'react'

import {
  initKnapWidget,
  type IKnapWidget,
} from 'src/app/ask-ai/adapters/knap-widget'
import { useAuthStore } from 'src/app/auth/store/auth-store'

export type KnapWidgetStatus = 'idle' | 'loading' | 'ready' | 'error'
export interface IUseKnapWidgetParams {
  isOpen: boolean
}
export interface IUseKnapWidgetReturn {
  containerRef: React.RefObject<HTMLDivElement>
  status: KnapWidgetStatus
  retry: () => void
}

export const useKnapWidget = ({
  isOpen,
}: IUseKnapWidgetParams): IUseKnapWidgetReturn => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<IKnapWidget | null>(null)
  const widgetTokenRef = useRef<string | null>(null)
  const owner = useRef(0)
  const initialization = useRef<AbortController | null>(null)
  const refresh = useRef<{ token: string; sequence: number } | null>(null)
  const refreshSequence = useRef(0)
  const failedRefreshToken = useRef<string | null>(null)
  const [openedOnce, setOpenedOnce] = useState(false)
  const [status, setStatus] = useState<KnapWidgetStatus>('idle')
  const [initAttempt, setInitAttempt] = useState(0)
  const token = useAuthStore(state => state.currentUser?.token)
  const email = useAuthStore(state => state.currentUser?.data.email)
  const name = useAuthStore(state => state.currentUser?.data.name ?? null)
  const restoreStatus = useAuthStore(state => state.restoreStatus)
  const setRestoreStatus = useAuthStore(state => state.setRestoreStatus)
  const latestToken = useRef(token)
  latestToken.current = token
  const hasToken = Boolean(token)

  const dispose = useCallback(() => {
    owner.current += 1
    initialization.current?.abort()
    initialization.current = null
    refresh.current = null
    failedRefreshToken.current = null
    widgetRef.current?.destroy()
    widgetRef.current = null
    widgetTokenRef.current = null
  }, [])

  // Logout, identity replacement and unmount revoke every pending continuation.
  useEffect(() => {
    dispose()
    setStatus('idle')
    if (!hasToken) setOpenedOnce(false)
    return dispose
  }, [dispose, email, hasToken])

  useEffect(() => {
    if (isOpen && hasToken) setOpenedOnce(true)
  }, [isOpen, hasToken])

  useEffect(() => {
    if (!hasToken || !openedOnce || widgetRef.current || !containerRef.current)
      return
    if (restoreStatus === 'idle' || restoreStatus === 'restoring') {
      setStatus('loading')
      return
    }
    const initialToken = latestToken.current
    if (restoreStatus === 'retryable-error' || !initialToken || !email) {
      setStatus('error')
      return
    }
    const controller = new AbortController()
    initialization.current = controller
    const generation = owner.current
    setStatus('loading')
    initKnapWidget({
      container: containerRef.current,
      accessToken: initialToken,
      user: { email, name },
      theme: 'light',
      initializationSignal: controller.signal,
    })
      .then(instance => {
        if (controller.signal.aborted || owner.current !== generation) {
          instance.destroy()
          return
        }
        widgetRef.current = instance
        widgetTokenRef.current = initialToken
        initialization.current = null
        setStatus('ready')
      })
      .catch(() => {
        if (!controller.signal.aborted && owner.current === generation)
          setStatus('error')
      })
    return () => controller.abort()
    // Visibility changes must not cancel an initialization already owned here.
  }, [openedOnce, hasToken, token, email, name, restoreStatus, initAttempt])

  useEffect(() => {
    const instance = widgetRef.current
    if (
      !token ||
      !instance ||
      (widgetTokenRef.current === token && !refresh.current) ||
      (status === 'error' && failedRefreshToken.current === token)
    )
      return
    if (refresh.current?.token === token) return
    const sequence = ++refreshSequence.current
    const generation = owner.current
    refresh.current = { token, sequence }
    setStatus('loading')
    instance
      .update({ accessToken: token })
      .then(() => {
        if (
          owner.current !== generation ||
          widgetRef.current !== instance ||
          refresh.current?.sequence !== sequence ||
          latestToken.current !== token
        )
          return
        widgetTokenRef.current = token
        failedRefreshToken.current = null
        refresh.current = null
        setStatus('ready')
      })
      .catch(() => {
        if (
          owner.current !== generation ||
          widgetRef.current !== instance ||
          refresh.current?.sequence !== sequence
        )
          return
        refresh.current = null
        failedRefreshToken.current = token
        setStatus('error')
      })
  }, [token, status])

  useEffect(() => {
    // Overlays block pointer input; inert also blocks keyboard/focus access to
    // the retained Shadow DOM while closed, restoring or refreshing auth.
    if (containerRef.current)
      containerRef.current.inert = !isOpen || status !== 'ready'
  }, [isOpen, status])

  const retry = useCallback(() => {
    if (status !== 'error') return
    dispose()
    setStatus('loading')
    if (restoreStatus === 'retryable-error') setRestoreStatus('idle')
    setInitAttempt(attempt => attempt + 1)
  }, [dispose, restoreStatus, setRestoreStatus, status])

  return { containerRef, status, retry }
}
